import PropTypes from "prop-types";
import { Modal } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import "../../style/BaseDialog.css";

// reusable base dialog component for modals with modern design
const BaseDialog = ({
  open,
  onClose,
  children,
  darkMode = false,
  title,
  actions,
  icon, // optional icon object: { icon: faIcon, variant: 'warning'|'danger'|'success'|'info' }
  size = "sm", // "sm" (500px) | "lg" (900px)
  showCloseButton = false,
  ariaLabelledBy = "dialog-title",
  ariaDescribedBy = "dialog-description",
}) => {
  const sizeClass = size === "lg" ? " base-dialog-container--lg" : "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      <div className={`base-dialog-container${sizeClass} ${darkMode ? "dark-mode" : ""}`}>
        {/* Close button (optional) */}
        {showCloseButton && (
          <button
            className="base-dialog-close"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}

        {/* Icon (optional - for confirmation dialogs) */}
        {icon && (
          <div
            className={`base-dialog-icon base-dialog-icon--${
              icon.variant || "info"
            }`}
          >
            <FontAwesomeIcon icon={icon.icon} />
          </div>
        )}

        {/* Title (if provided) */}
        {title && (
          <>
            <h2 id={ariaLabelledBy} className="base-dialog-title">
              {title}
            </h2>
            <div className="base-dialog-divider" />
          </>
        )}

        {/* Content */}
        <div id={ariaDescribedBy} className="base-dialog-content">
          {children}
        </div>

        {/* Actions (if provided) */}
        {actions && <div className="base-dialog-actions">{actions}</div>}
      </div>
    </Modal>
  );
};

BaseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  darkMode: PropTypes.bool,
  title: PropTypes.string,
  actions: PropTypes.node,
  icon: PropTypes.shape({
    icon: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(["warning", "danger", "success", "info"]),
  }),
  size: PropTypes.oneOf(["sm", "lg"]),
  showCloseButton: PropTypes.bool,
  ariaLabelledBy: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
};

export default BaseDialog;
